import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CalendarDays, X } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { api } from "@/lib/api";

export const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="conteudos" data-testid="articles-section" className="bg-cob-beige/50 py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          overline="Conteúdos"
          title="Informação também faz parte do cuidado."
          text="Artigos educativos sobre saúde, movimento, prevenção e qualidade de vida."
        />

        {loading ? (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-[380px] rounded-2xl bg-white/70 animate-pulse" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <p className="mt-14 text-center text-cob-muted" data-testid="articles-empty-state">
            Em breve, novos conteúdos.
          </p>
        ) : (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {articles.map((article, i) => (
              <Reveal key={article.id} delay={(i % 3) * 0.09} className="h-full">
                <article
                  data-testid={`article-card-${i + 1}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_-22px_rgba(6,63,50,0.22)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_26px_50px_-24px_rgba(6,63,50,0.38)]"
                >
                  {article.image && (
                    <div className="relative h-48 overflow-hidden">
                      <img loading="lazy" decoding="async"
                        src={article.image}
                        alt={article.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                      />
                      {article.category && (
                        <span className="absolute left-4 top-4 rounded-full bg-cob-dark/90 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-cob-gold backdrop-blur-sm">
                          {article.category}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    {article.date && (
                      <p className="inline-flex items-center gap-1.5 text-[12px] font-medium text-cob-muted">
                        <CalendarDays className="h-3.5 w-3.5 text-cob-gold" /> {article.date}
                      </p>
                    )}
                    <h3 className="mt-2.5 font-serif text-xl font-semibold leading-snug text-cob-dark">
                      {article.title}
                    </h3>
                    <p className="mt-2.5 flex-1 text-sm leading-relaxed text-cob-muted">{article.summary}</p>
                    <button
                      type="button"
                      data-testid={`article-read-more-${i + 1}`}
                      onClick={() => setActive(article)}
                      className="mt-5 inline-flex items-center gap-1.5 self-start text-[13px] font-semibold text-cob-green transition-colors hover:text-cob-dark"
                    >
                      Leia mais
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="article-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-cob-dark/60 backdrop-blur-sm p-0 sm:p-6"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
              className="relative max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl"
            >
              {active.image && (
                <img loading="lazy" decoding="async" src={active.image} alt={active.title} className="h-56 sm:h-64 w-full object-cover" />
              )}
              <button
                type="button"
                data-testid="article-modal-close"
                aria-label="Fechar artigo"
                onClick={() => setActive(null)}
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-cob-dark shadow-md transition-colors hover:bg-cob-dark hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="p-7 sm:p-9">
                {active.category && (
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-cob-gold">
                    {active.category}
                  </span>
                )}
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-cob-dark">
                  {active.title}
                </h3>
                {active.date && <p className="mt-2 text-[13px] text-cob-muted">{active.date}</p>}
                <div className="mt-5 space-y-4">
                  {(active.content || active.summary).split("\n").filter(Boolean).map((p, i) => (
                    <p key={i} className="text-[15px] leading-relaxed text-cob-muted">
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
